import Vue from 'vue'
import VueRouter, { isNavigationFailure } from 'vue-router'
import * as Sentry from '@sentry/browser'

import store from '@/store'
import { loadLanguageAsync } from '@/i18n'
import LandingSwap from '@/components/LandingSwap'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'

import subscriptionRoutes from '@/router/account/subscriptionRoutes'
import manageSubscriptionRoutes from '@/router/account/manageSubscriptionRoutes'
import bulkScreensRoutes from '@/router/bulkScreensRoutes'
import warmingUpEmotionalCourseRoutes from '@/experiments/DEV-2622/routes/warmingUpEmotionalCourseRoutes'

Vue.use(VueRouter)

const routes = [
	{
		path: '/',
		name: 'landing',
		component: LandingSwap,
		meta: {
			isLanding: true,
			hideHeader: true
		}
	},

	{
		path: '/men',
		name: 'male-funnel',
		component: LandingSwap,
		meta: {
			isLanding: true
		}
	},

	{
		path: '/women',
		name: 'female-funnel',
		component: LandingSwap,
		meta: {
			isLanding: true
		}
	},

	{
		path: '/activity',
		name: 'activity-funnel',
		component: LandingSwap,
		meta: {
			isLanding: true
		}
	},

	{
		path: '/account',
		redirect: '/survey/account'
	},

	{
		path: '/men/survey/:page*',
		name: 'male-survey',
		component: () => import(/* webpackChunkName: "new-survey" */ '@/views/StrapiSurvey.vue')
	},

	{
		path: '/women/survey/:page*',
		name: 'female-survey',
		component: () => import(/* webpackChunkName: "new-survey" */ '@/views/StrapiSurvey.vue')
	},

	{
		path: '/youtube/survey/:page*',
		name: 'youtube-survey',
		component: () => import(/* webpackChunkName: "new-survey" */ '@/views/StrapiSurvey.vue')
	},

	{
		path: '/activity/survey/:page*',
		name: 'activity-survey',
		component: () => import(/* webpackChunkName: "new-survey" */ '@/views/StrapiSurvey.vue')
	},

	...subscriptionRoutes,

	{
		path: '/survey/',
		name: 'survey',
		component: () => import(/* webpackChunkName: "srv" */ '../views/Survey.vue'),
		redirect: {
			name: 'weight-loss-goals'
		},

		children: [
			...manageSubscriptionRoutes,
			...bulkScreensRoutes,
			...warmingUpEmotionalCourseRoutes,
			{
				path: 'chatbot',
				name: 'chatbot',
				component: () =>
					import(/* webpackChunkName: "chatbot" */ '../experiments/DEV-6297/views/ChatbotActivation.vue'),
				meta: {
					requiresAuth: true,
					requiresUser: true,
					requiresOnboarding: true
				}
			},

			{
				path: 'faq',
				name: 'faq',
				component: () => import(/* webpackChunkName: "srv" */ '../views/Faq.vue'),
				meta: {
					hideHeader: true
				}
			},

			{
				path: 'contact-us',
				name: 'contact-us',
				component: () => import(/* webpackChunkName: "srv" */ '../views/ContactUs.vue'),
				meta: {
					hideHeader: true
				}
			},

			{
				path: 'intermittent-fasting',
				name: 'intermittent-fasting',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/IntermittentFasting.vue'),
				meta: {
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingIntermittentFastingScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingIntermittentFastingScreenDone'
					}
				}
			},

			{
				path: 'intermittent-fasting-about',
				name: 'intermittent-fasting-about',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/IntermittentFastingAbout.vue'),
				meta: {
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFastingAboutScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFastingAboutScreenDone'
					}
				}
			},

			{
				path: 'your_current_state',
				name: 'your_current_state',
				component: () => import('../views/survey/YourCurrentState.vue'),
				meta: {
					hideProgress: true
				}
			},

			{
				path: 'your_current_state_1',
				name: 'your_current_state_1',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/YourCurrentState2-1.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingProfileScreen'
						}
					}
				}
			},

			{
				path: 'your_current_state_2',
				name: 'your_current_state_2',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/YourCurrentState2-2.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingWeightGoalScreen'
						}
					}
				}
			},

			{
				path: 'regular_diet_vs_simple',
				name: 'regular_diet_vs_simple',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/RegularDietVsSimple.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingRegularDietScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingRegularDietScreenDone'
					}
				}
			},

			{
				path: 'your_body_type',
				name: 'your_body_type',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/YourBodyType.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingBodyTypeScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingBodyTypeScreenDone'
					}
				}
			},

			{
				path: 'do_you_eat_carbs',
				name: 'do_you_eat_carbs',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/DoYouEatCarbs.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingHighCarbFoodScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingHighCarbFoodScreenDone'
					}
				}
			},

			{
				path: 'sticking_to_plan',
				name: 'sticking_to_plan',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/StickingToPlan.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingStickingToPlanScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingStickingToPlanScreenDone'
					}
				}
			},

			{
				path: 'how_much_water_you_drink',
				name: 'how_much_water_you_drink',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/HowMuchWaterYouDrink.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingWaterScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingWaterScreenDone'
					}
				}
			},

			{
				path: 'any_specific_diet',
				name: 'any_specific_diet',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/AnySpecificDiet.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingDietRestrictionsScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingDietRestrictionsScreenDone',
						userProperty: 'Diet restrictions'
					}
				}
			},

			{
				path: 'number_of_meals',
				name: 'number_of_meals',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/NumberOfMeals.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingMainMealsCountScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingMainMealsCountScreenDone',
						userProperty: 'Meals Count'
					}
				}
			},

			{
				path: 'whats_your_job_like',
				name: 'whats_your_job_like',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/WhatsYourJobLike.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingWorkTypeScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingWorkTypeScreenDone',
						userProperty: 'Work Type'
					}
				}
			},

			{
				path: 'how_often_do_you_work_out',
				name: 'how_often_do_you_work_out',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/HowOftenDoYouWorkOut.vue')
			},

			{
				path: 'how_do_you_sleep',
				name: 'how_do_you_sleep',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/HowDoYouSleep.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingSleepScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingSleepScreenDone'
					}
				}
			},

			{
				path: 'any_medical_conditions',
				name: 'any_medical_conditions',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/AnyMedicalConditions.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingHealthConditionsScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingHealthConditionsScreenDone',
						userProperty: 'Conditions'
					}
				}
			},

			{
				path: 'do_you_take_any_medication',
				name: 'do_you_take_any_medication',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/DoYouTakeAnyMedication.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingDrugsScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingDrugsScreenDone',
						userProperty: 'Drugs'
					}
				}
			},

			{
				path: 'spend-on-losing-weight',
				name: 'spend-on-losing-weight',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/SpendOnLosingWeight.vue'),
				meta: {
					hideProgress: true
				}
			},

			{
				path: 'gender',
				name: 'gender',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Gender.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingGenderScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingGenderScreenDone',
						userProperty: 'Sex'
					}
				}
			},

			{
				path: 'age',
				name: 'age',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Age.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingAgeScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingAgeScreenDone',
						property: 'age',
						isData: true
					}
				}
			},

			{
				path: 'trust',
				name: 'trust',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Trust.vue'),
				meta: {
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingTrustScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingTrustScreenDone'
					}
				}
			},

			{
				path: 'processing',
				name: 'processing',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Processing.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingLoaderWebScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingLoaderWebScreenDone'
					}
				}
			},

			{
				path: 'processing-email',
				name: 'processing-email',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Processing.vue'),
				props: { scenarioId: 'email-retarget' },
				meta: {
					requiresUser: true,
					requiresOnboarding: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingLoaderWebScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingLoaderWebScreenDone'
					}
				}
			},

			{
				path: 'additional-question-1',
				name: 'additional-question-1',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/RelatedQuestion_1.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingAdditionalQuestion1Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingAdditionalQuestion1ScreenDone'
					}
				}
			},

			{
				path: 'additional-question-2',
				name: 'additional-question-2',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/RelatedQuestion_2.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingAdditionalQuestion2Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingAdditionalQuestion2ScreenDone'
					}
				}
			},

			{
				path: 'additional-question-3',
				name: 'additional-question-3',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/RelatedQuestion_3.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingAdditionalQuestion3Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingAdditionalQuestion3ScreenDone'
					}
				}
			},

			{
				path: 'additional-question-4',
				name: 'additional-question-4',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/RelatedQuestion_4.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingAdditionalQuestion4Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingAdditionalQuestion4ScreenDone'
					}
				}
			},

			{
				path: 'following-statement-1',
				name: 'following-statement-1',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/FollowingStatement_1.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFollowingStatement1Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFollowingStatement1ScreenDone'
					}
				}
			},

			{
				path: 'following-statement-2',
				name: 'following-statement-2',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/FollowingStatement_2.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFollowingStatement2Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFollowingStatement2ScreenDone'
					}
				}
			},

			{
				path: 'following-statement-3',
				name: 'following-statement-3',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/FollowingStatement_3.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFollowingStatement3Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFollowingStatement3ScreenDone'
					}
				}
			},

			{
				path: 'following-statement-4',
				name: 'following-statement-4',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/FollowingStatement_4.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFollowingStatement4Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFollowingStatement4ScreenDone'
					}
				}
			},

			{
				path: 'following-statement-5',
				name: 'following-statement-5',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/FollowingStatement_5.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFollowingStatement5Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFollowingStatement5ScreenDone'
					}
				}
			},

			{
				path: 'following-statement-6',
				name: 'following-statement-6',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/FollowingStatement_6.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingFollowingStatement6Screen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingFollowingStatement6ScreenDone'
					}
				}
			},

			{
				path: 'storytelling',
				name: 'storytelling',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Storytelling.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingStorytellingScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingStorytellingScreenDone'
					}
				}
			},

			{
				path: 'stats',
				name: 'stats',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Stats.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingStatsScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingStatsScreenDone'
					}
				}
			},

			{
				path: 'motivation-events',
				name: 'motivation-events',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/MotivationEvents.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingMotivationEventScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingMotivationEventScreenDone',
						extraProperties: [
							{
								name: 'other_input',
								property: 'motivation_event_custom',
								isData: true
							}
						]
					}
				}
			},

			{
				path: 'motivation-events-date',
				name: 'motivation-events-date',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/MotivationEventsDate.vue'),
				meta: {
					oneSectionProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingMotivationEventDateScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingMotivationEventDateScreenDone'
					}
				}
			},

			{
				path: 'analysing-loader',
				name: 'analysing-loader',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/AnalysingLoader.vue'),
				meta: {
					hideHeader: true,
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingLoaderPreparingProgramScreen'
						}
					}
				}
			},

			{
				path: 'email',
				name: 'email',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/Email.vue'),
				meta: {
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingResultCompleteScreen'
						}
					}
				}
			},

			{
				path: 'goal-without-plan',
				name: 'goal-without-plan',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/GoalWithoutPlan.vue'),
				meta: {
					hideHeader: true,
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingGoalWithoutPlanBannerScreen'
						}
					}
				}
			},

			{
				path: 'weight-loss-speed',
				name: 'weight-loss-speed',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/WeightLossSpeed.vue'),
				meta: {
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingWeightLossSpeedScreen'
						}
					}
				}
			},

			{
				path: 'invite-friends',
				name: 'invite-friends',
				component: () => import(/* webpackChunkName: "srv" */ '../views/survey/InviteFriends.vue'),
				meta: {
					hideHeader: true,
					hideProgress: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingInviteFriendsScreen'
						}
					}
				}
			},

			{
				path: 'result',
				name: 'result',
				component: () => import(/* webpackChunkName: "result" */ '../views/Result.vue'),
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingResultScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingResultScreenDone'
					}
				}
			},

			{
				path: 'result_complete',
				name: 'result_complete',
				component: () => import(/* webpackChunkName: "result" */ '../views/ResultComplete.vue'),
				meta: {
					requiresUser: true
				}
			},

			{
				path: 'health_notice',
				name: 'health_notice',
				component: () => import(/* webpackChunkName: "result" */ '../views/HealthNotice.vue'),
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingHealthNoticeScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'OnboardingHealthNoticeScreenDone'
					}
				}
			},

			{
				path: 'plan_ready',
				name: 'plan_ready',
				component: () => import(/* webpackChunkName: "paywall" */ '../views/PlanReady.vue'),
				meta: {
					hideHeader: true,
					requiresOnboarding: true,
					requiresUser: true,
					requiresUserFlags: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingCreatedProgramScreen'
						}
					}
				}
			},

			{
				path: 'plan_ready_activity',
				name: 'plan_ready_activity',
				component: () => import(/* webpackChunkName: "paywall-activity" */ '../views/PlanReadyActivity.vue'),
				meta: {
					hideHeader: true,
					requiresOnboarding: true,
					requiresUser: true,
					requiresUserFlags: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingCreatedProgramScreen'
						}
					}
				}
			},

			{
				path: 'login',
				name: 'create_account',
				component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
				meta: {
					// onlyGuest: true,
					requiresUser: true,
					gtm: 'login'
				}
			},

			{
				path: 'login_by_code',
				name: 'login_by_code',
				component: () => import(/* webpackChunkName: "login_by_code" */ '../views/LoginByCode.vue'),
				meta: {
					requiresUser: true
				}
			},

			{
				path: 'download',
				name: 'download',
				component: () => import(/* webpackChunkName: "update-profile" */ '../views/download/Index'),
				meta: {
					// requiresAuth: true,
					hideProgress: true,
					requiresUser: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'DownloadAppScreen'
						}
					}
				}
			},

			{
				path: 'account',
				name: 'account',
				component: () => import(/* webpackChunkName: "account" */ '../views/account/Account.vue'),
				meta: {
					requiresAuth: true,
					requiresSubscription: true,
					requiresUser: true,
					requiresUserFlags: true,
					hideHeader: true
				}
			},

			{
				path: 'cancel_subscription',
				name: 'cancel_subscription',
				component: () => import(/* webpackChunkName: "account" */ '../views/account/CancelSubscription.vue'),
				meta: {
					requiresAuth: true,
					requiresSubscription: true,
					requiresUser: true
				}
			},

			{
				path: 'cancel_request',
				name: 'cancel_request',
				component: () => import(/* webpackChunkName: "account" */ '../views/account/CancelSubscriptionRequest.vue'),
				meta: {
					requiresAuth: true,
					requiresSubscription: true,
					requiresUser: true
				}
			},

			{
				path: 'cancel_result',
				name: 'cancel_result',
				component: () => import(/* webpackChunkName: "account" */ '../views/account/CancelSubscriptionResponse.vue'),
				meta: {
					requiresAuth: true,
					requiresUser: true
				}
			},
			{
				path: 'confirm_email',
				name: 'confirm_email',
				component: () => import(/* webpackChunkName: "account" */ '../views/account/ConfirmEmail.vue'),
				meta: {
					hideHeader: true,
					requiresUser: true
				}
			},

			{
				path: 'change_email',
				name: 'change_email',
				component: () => import(/* webpackChunkName: "account" */ '../views/account/ChangeEmail.vue'),
				meta: {
					requiresUser: true
				}
			},

			{
				path: 'special-upsale',
				name: 'special-upsale',
				component: () => import(/* webpackChunkName: "upsell" */ '../views/upsell/SpecialUpsale.vue'),
				meta: {
					requiresUser: true,
					requiresSubscription: true,
					hideProgress: true
				}
			},

			{
				path: 'upsell',
				name: 'upsell',
				component: () => import(/* webpackChunkName: "upsell" */ '../views/upsell/Upsell.vue'),
				meta: {
					requiresUser: true,
					hideProgress: true,
					requiresSubscription: true,
					hideHeader: true
				}
			},

			{
				path: 'upsell-zing-body-scan',
				name: 'upsell-zing-body-scan',
				component: () => import(/* webpackChunkName: "upsell" */ '../views/upsell/Upsell.vue'),
				props: { scenarioId: 'zingBodyScan' },
				meta: {
					requiresUser: true,
					hideProgress: true,
					requiresSubscription: true,
					hideHeader: true
				}
			},

			{
				path: 'upsell-assessment-workbooks',
				name: 'upsell-assessment-workbooks',
				component: () => import(/* webpackChunkName: "upsell" */ '../views/upsell/Upsell.vue'),
				props: { scenarioId: 'assessmentWorkbooks' },
				meta: {
					requiresUser: true,
					hideProgress: true,
					requiresSubscription: true,
					hideHeader: true
				}
			},

			{
				path: 'post-purchase',
				name: 'post-purchase',
				component: () => import(/* webpackChunkName: "upsell" */ '../views/PostPurchase.vue'),
				meta: {
					requiresUser: true,
					requiresSubscription: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingWhereYouHearScreen'
						}
					}
				}
			},

			{
				path: 'update-profile',
				name: 'update-profile',
				component: () => import(/* webpackChunkName: "update-profile" */ '../views/UpdateProfile.vue'),
				meta: {
					hideProgress: true,
					requiresUser: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'UpdateProfileScreen'
						}
					},
					pageAnalyticLogicEvent: {
						name: 'UpdateProfileScreenDone'
					}
				}
			},

			{
				path: 'pregnancy',
				name: 'pregnancy',
				component: () => import(/* webpackChunkName: "onboarding" */ '../views/survey/Pregnancy.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingPregnancyScreen'
						}
					}
				}
			},
			{
				path: 'eating-disorder',
				name: 'eating-disorder',
				component: () => import(/* webpackChunkName: "onboarding" */ '../views/survey/EatingDisorder.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingEatingDisordersScreen'
						}
					}
				}
			},
			{
				path: 'name',
				name: 'name',
				component: () => import(/* webpackChunkName: "onboarding" */ '../views/survey/Name.vue'),
				meta: {
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'OnboardingNameScreen'
						}
					}
				}
			}
		]
	},

	{
		path: '/new-survey/:page*',
		name: 'new-survey',
		component: () => import(/* webpackChunkName: "new-survey" */ '@/views/StrapiSurvey.vue')
	},

	{
		path: '/bmi-calculator',
		name: 'bmi-calculator',
		component: () =>
			import(/* webpackChunkName: "bmi-caclulator" */ '../experiments/DEV-2476/views/BmiCalculatorPage.vue')
	},

	{
		path: '*',
		name: '404',
		component: () => import(/* webpackChunkName: "not-found" */ '@/views/EmptyPage.vue')
	}
]

const router = new VueRouter({
	mode: 'history',
	// base: process.env.BASE_URL,
	routes,
	scrollBehavior(to, from, savedPosition) {
		return (
			savedPosition || {
				x: 0,
				y: 0
			}
		)
	}
})

router.beforeEach(async (to, from, next) => {
	const blockingRequests = [
		store.dispatch('coreFeatures/hydrateCoreFeatures'),
		store.dispatch('fetchGeo', {
			country: to.query.country
		}),
		loadLanguageAsync()
	]

	if (!store.state.user) {
		const fetchUserRequest = store.dispatch('fetchUserFromAws')

		if (to.meta.requiresUser) {
			blockingRequests.push(fetchUserRequest)
		}
	}

	if (to.meta.requiresUserFlags) {
		blockingRequests.push(store.dispatch('userFlags/fetchUserFlags'))
	}

	await Promise.all(blockingRequests)
	store.commit('status/setResourceReadiness', 'network')

	let isAuthenticated = store.getters.isAuthenticated
	let isOnboarded = store.getters.isOnboarded

	let ls_redirect = localStorage.getItem('redirect')

	if (ls_redirect && !to.meta.onlyGuest) {
		localStorage.removeItem('redirect')
		next({
			name: ls_redirect
		})
		return
	}

	if (to.meta.onlyGuest && isAuthenticated) {
		if (to.query.redirect) {
			next({
				name: to.query.redirect
			})
			return
		} else {
			next({
				name: 'account'
			})
			return
		}
	}

	if (to.meta.requiresAuth && !isAuthenticated) {
		next({
			name: 'create_account',
			query: {
				redirect: to.name
			}
		})
		return
	}

	if (to.meta.requiresOnboarding && !isOnboarded) {
		next({
			name: 'survey',
			query: {
				track: true
			},
			replace: true
		})
		return
	}

	if (to.meta.requiresSubscription && !store.state.subscription && isAuthenticated) {
		next({
			name: 'plan_ready',
			replace: true
		})
		return
	}

	next()
})

const originalPushMethod = VueRouter.prototype.push
const originalReplaceMethod = VueRouter.prototype.replace
function navigationChangedHandler(originalMethod, location, onComplete, onAbort) {
	if (onComplete || onAbort) {
		return originalMethod.call(this, location, onComplete, onAbort)
	}

	return originalMethod.call(this, location).catch((err) => {
		// Ignore the NavigationDuplicated error on the console
		if (err.name === 'NavigationDuplicated') {
			return
		}

		if (ENV_DEV_OR_STAGING) {
			console.warn(err)
			Sentry.captureException(err)
		}

		if (isNavigationFailure(err)) {
			return err
		}

		return Promise.reject(err)
	})
}
VueRouter.prototype.push = function (location, onComplete, onAbort) {
	return navigationChangedHandler.call(this, originalPushMethod, location, onComplete, onAbort)
}

VueRouter.prototype.replace = function replace(location, onComplete, onAbort) {
	return navigationChangedHandler.call(this, originalReplaceMethod, location, onComplete, onAbort)
}

router.onError((error) => {
	if (error.name === 'ChunkLoadError') {
		Sentry.withScope((scope) => {
			scope.setTags({
				chunkLoadErrorRequest: error.request
			})
			Sentry.captureException(error)
		})
	}
})

export default router
